import React, { useRef, useState } from 'react';
import { I } from '@/lib/icons';
import { useWorkspace, useExpenses } from '@/hooks/useData';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { toIsoDate } from '@/lib/format';
import { uploadFileToDrive, isDriveConnected, DriveNotConnectedError, DriveAuthRevokedError } from '@/lib/driveUpload';
import { compressImage } from '@/lib/imageCompress';

export default function QuickCaptureFab() {
  const { workspace } = useWorkspace();
  const { user } = useAuth();
  const { create } = useExpenses(workspace?.id);
  const toast = useToast();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!workspace || !user) return null;

  const handleClick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const connected = await isDriveConnected();
      if (!connected) {
        toast.error('Najprv pripoj Google Drive v Nastaveniach.');
        return;
      }
      // Q-IMG-1: pre-compress images before Drive upload (PDFs/non-images skip).
      const compressed = await compressImage(file);
      const uploadFile = compressed.file;
      if (!compressed.skipped && compressed.compressedSize < compressed.originalSize) {
        const ratio = compressed.compressedSize / compressed.originalSize;
        if (ratio < 0.5) {
          const fromMb = (compressed.originalSize / (1024 * 1024)).toFixed(1);
          const toKb = Math.round(compressed.compressedSize / 1024);
          console.info(`[Q-IMG-1] Optimizované: ${fromMb}MB → ${toKb}KB (${Math.round(ratio * 100)}%)`);
        }
      }
      const result = await uploadFileToDrive({
        file: uploadFile,
        workspace_id: workspace.id,
        year: new Date().getFullYear(),
        type: 'vydavky',
      });
      await create({
        vendor_name: null, vendor_ico: null, amount: 0,
        dph_rate: workspace.default_dph_rate || 0, dph_amount: 0,
        date: toIsoDate(), category: 'other',
        description: 'Rýchly záber — doplň polia.',
        paid_via: 'card',
        gdrive_file_id: result.gdrive_file_id,
      });
      toast.success('Doklad uložený do Drive. Polia doplníš v sekcii Výdavky.');
    } catch (err) {
      if (err instanceof DriveNotConnectedError || err instanceof DriveAuthRevokedError) {
        toast.error(err.message);
      } else {
        toast.error('Upload zlyhal: ' + (err.message || err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        data-testid="quick-capture-fab"
        aria-label="Rýchly záber dokladu"
        title="Rýchly záber dokladu"
        style={{
          position: 'fixed', right: 24, bottom: 24, zIndex: 900,
          width: 56, height: 56, borderRadius: '50%',
          background: busy ? 'var(--text-muted)' : 'var(--menu-btn-bg)',
          color: 'var(--menu-btn-fg)', border: 'none', cursor: busy ? 'wait' : 'pointer',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.15s var(--ease)',
          fontFamily: 'inherit',
        }}
        onMouseEnter={e => { if (!busy) e.currentTarget.style.transform = 'scale(1.05)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
      >
        {busy
          ? <span className="cd-spinner" style={{ borderTopColor: 'var(--menu-btn-fg)' }} />
          : <I.camera width={22} height={22} />}
      </button>
    </>
  );
}
